import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search,
  Users,
  MapPin,
  Globe,
  Coffee,
  Building2,
  ArrowRight,
  Filter,
  X,
  Loader2,
  Sparkles,
  UserPlus,
  Send,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

const DiscoverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useAuth();
  
  // State
  const [communities, setCommunities] = useState([]);
  const [circles, setCircles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedCommunity, setSelectedCommunity] = useState(searchParams.get('community') || '');
  const [activeTab, setActiveTab] = useState('circles');
  const [showFilters, setShowFilters] = useState(false);
  
  // Join Request Modal State
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCircle, setJoinCircle] = useState(null); // {id, name}
  const [justification, setJustification] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // (Sprint 7 agent search moved to /networkbot - Agents tab)

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/discover/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch communities
  const fetchCommunities = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCity) params.append('city', selectedCity);
      params.append('limit', '50');
      
      const res = await axios.get(`${API}/api/discover/communities?${params.toString()}`);
      setCommunities(res.data.communities || []);
    } catch (err) {
      console.error('Failed to fetch communities:', err);
    }
  }, [searchTerm, selectedCity]);

  // Fetch circles
  const fetchCircles = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCommunity) params.append('community_id', selectedCommunity);
      params.append('limit', '50');
      
      const res = await axios.get(`${API}/api/discover/circles?${params.toString()}`);
      setCircles(res.data.circles || []);
    } catch (err) {
      console.error('Failed to fetch circles:', err);
    }
  }, [searchTerm, selectedCommunity]);

  // Initial load
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchCommunities(), fetchCircles()]);
      if (isMounted) setLoading(false);
    };
    loadData();
    return () => { isMounted = false; };
  }, [fetchStats, fetchCommunities, fetchCircles]);

  // Check if user came back from login with a pending circle join
  useEffect(() => {
    // Only run when user is logged in and circles are loaded
    if (!user || !token || circles.length === 0) {
      return;
    }
    
    // Check URL param first
    const pendingCircleId = searchParams.get('circle');
    
    // Also check sessionStorage (more reliable after login redirect)
    const storedPendingCircle = sessionStorage.getItem('pendingJoinCircle');
    
    let circleToJoin = null;
    
    // Priority 1: URL param
    if (pendingCircleId) {
      circleToJoin = circles.find(c => c.id === pendingCircleId);
      // Clear URL param
      searchParams.delete('circle');
      setSearchParams(searchParams, { replace: true });
    }
    // Priority 2: SessionStorage
    else if (storedPendingCircle) {
      try {
        const parsed = JSON.parse(storedPendingCircle);
        
        // Find the circle in our list
        circleToJoin = circles.find(c => c.id === parsed.id);
        
        // Even if circle not found in current list, we can still show modal with stored info
        if (!circleToJoin && parsed.id && parsed.name) {
          circleToJoin = { id: parsed.id, name: parsed.name };
        }
        
        // Clear sessionStorage
        sessionStorage.removeItem('pendingJoinCircle');
      } catch (e) {
        console.error('[DiscoverPage] Failed to parse pending circle:', e);
        sessionStorage.removeItem('pendingJoinCircle');
      }
    }
    
    if (circleToJoin) {
      // Open the join modal with justification form
      setJoinCircle({ id: circleToJoin.id, name: circleToJoin.name });
      setJustification('');
      setShowJoinModal(true);
    }
  }, [user, token, circles, searchParams, setSearchParams]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedCommunity) params.set('community', selectedCommunity);
    setSearchParams(params);
    fetchCommunities();
    fetchCircles();
  };

  // (Sprint 7 agent search moved to /networkbot)

  // Handle join request - open modal
  const handleJoinCircle = (circleId, circleName) => {
    if (!user || !token) {
      // Store the circle ID in sessionStorage for after login
      const pendingData = { id: circleId, name: circleName };
      sessionStorage.setItem('pendingJoinCircle', JSON.stringify(pendingData));
      toast.info(`Login to request access to "${circleName}"`);
      navigate(`/login?redirect=discover`);
      return;
    }
    
    // Open join modal with justification form
    setJoinCircle({ id: circleId, name: circleName });
    setJustification('');
    setShowJoinModal(true);
  };

  // Submit the join request with justification
  const submitJoinRequest = async () => {
    if (!joinCircle || !justification.trim()) {
      toast.error('Please tell us why you want to join this circle');
      return;
    }

    setSubmittingRequest(true);
    try {
      const res = await axios.post(
        `${API}/api/circles/${joinCircle.id}/request-access`,
        { justification: justification.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.status === 'approved') {
        toast.success('Welcome! You\'ve been auto-approved to join this circle.');
      } else if (res.data.status === 'rejected') {
        toast.error(res.data.message || 'Your request could not be approved at this time.');
      } else {
        toast.success('Request submitted! An admin will review your request.');
      }
      
      // Close modal
      setShowJoinModal(false);
      setJoinCircle(null);
      setJustification('');
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info(err.response.data.detail || 'You already have a pending request or are a member.');
        setShowJoinModal(false);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to submit request');
      }
    } finally {
      setSubmittingRequest(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCity('');
    setSelectedCommunity('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7] font-mono selection:bg-[#FFE135] selection:text-[#0F172A]" data-testid="discover-page">
      <Helmet>
        <title>Discover Professional Communities | Match It Up©</title>
        <meta name="description" content="Explore verified professional communities, Brew Circles, and networking groups on Match It Up© — India's intent-based B2B networking platform. Find your people by industry, city, or interest." />
        <link rel="canonical" href="https://matchitup.in/discover" />
        <meta property="og:title" content="Discover Professional Communities | Match It Up©" />
        <meta property="og:description" content="Explore verified professional communities and Brew Circles on Match It Up©. Find founders, investors, and operators in your city and industry." />
        <meta property="og:url" content="https://matchitup.in/discover" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Discover Professional Communities | Match It Up©" />
        <meta name="twitter:description" content="Find verified professional communities and networking groups in India — by city, industry, or interest." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Discover Professional Communities",
          "description": "Explore verified professional communities, Brew Circles, and networking groups on Match It Up© — India's intent-based B2B networking platform.",
          "url": "https://matchitup.in/discover",
          "inLanguage": "en-IN",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Match It Up©",
            "url": "https://matchitup.in"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://matchitup.in" },
              { "@type": "ListItem", "position": 2, "name": "Discover", "item": "https://matchitup.in/discover" }
            ]
          }
        })}</script>
      </Helmet>
      {/* Header with Logo */}
      <header className="sticky top-0 z-40 bg-[#FFFDF7] border-b-[4px] border-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" state={{ fromLogo: true }} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-home-link">
              <Logo size="sm" showText={true} textColor="text-[#0F172A]" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 px-4 py-2 text-[#0F172A] border border-slate-200 shadow-lg shadow-slate-200/40 hover:-translate-y-1 hover:shadow-none bg-white rounded-xl font-black uppercase tracking-tight transition-all"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-black uppercase tracking-tight">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[#FFE135] border-b-[4px] border-[#0F172A]">
        <div className="absolute inset-0 pattern-dots" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl mb-6">
              <Sparkles className="w-4 h-4 text-[#E8430A]" />
              <span className="text-sm text-[#0F172A] font-black uppercase tracking-tight">Discover Your Tribe</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0F172A] mb-6 uppercase tracking-tight">
              Find Your{' '}
              <span className="text-white relative inline-block">
                <span className="relative z-10">Community</span>
                <span className="absolute inset-0 bg-[#E8430A] -skew-x-6 z-0" />
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-[#0F172A] font-bold max-w-2xl mx-auto mb-10">
              Join circles of like-minded professionals. Network, collaborate, and grow together.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative z-20">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-grow">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search communities and circles..."
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl text-[#0F172A] font-bold placeholder-slate-400 focus:outline-none focus:translate-y-[2px] focus:shadow-lg shadow-slate-200/40 transition-all"
                    data-testid="discover-search"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-4 py-4 bg-white border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 rounded-xl text-[#0F172A] font-black uppercase tracking-tight transition-all flex items-center justify-center gap-2"
                >
                  <Filter className="w-5 h-5" />
                  <span className="sm:hidden">Filters</span>
                </button>
                <button
                  type="submit"
                  className="px-8 py-4 bg-[#E8430A] text-white border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 rounded-xl font-black uppercase tracking-tight transition-all"
                  data-testid="discover-search-btn"
                >
                  Search
                </button>
              </div>
              
              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-4 p-4 bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl text-left">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm text-[#0F172A] font-black uppercase tracking-tight mb-2">City</label>
                      <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        className="w-full px-4 py-2 bg-[#FFFDF7] border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl text-[#0F172A] font-bold focus:outline-none  focus:shadow-lg shadow-slate-200/50 transition-all appearance-none"
                      >
                        <option value="">All Cities</option>
                        {stats?.cities?.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-sm text-[#0F172A] font-black uppercase tracking-tight mb-2">Community</label>
                      <select
                        value={selectedCommunity}
                        onChange={(e) => setSelectedCommunity(e.target.value)}
                        className="w-full px-4 py-2 bg-[#FFFDF7] border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl text-[#0F172A] font-bold focus:outline-none  focus:shadow-lg shadow-slate-200/50 transition-all appearance-none"
                      >
                        <option value="">All Communities</option>
                        {communities.map(comm => (
                          <option key={comm.id} value={comm.id}>{comm.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="px-4 py-2 text-[#E8430A] font-black uppercase tracking-tight hover:bg-[#E8430A] hover:text-white border-[2px] border-transparent hover:border-[#0F172A] rounded-xl transition-all"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="border-b-[4px] border-[#0F172A] bg-white font-mono">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-wrap justify-center gap-8 sm:gap-16">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-[#0F172A] uppercase tracking-tight">{stats.total_communities}</div>
                <div className="text-sm text-[#0F172A] font-bold uppercase tracking-tight">Communities</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-[#0F172A] uppercase tracking-tight">{stats.total_circles}</div>
                <div className="text-sm text-[#0F172A] font-bold uppercase tracking-tight">Circles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-[#0F172A] uppercase tracking-tight">{stats.total_members}</div>
                <div className="text-sm text-[#0F172A] font-bold uppercase tracking-tight">Members</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-black text-[#0F172A] uppercase tracking-tight">{stats.cities?.length || 0}</div>
                <div className="text-sm text-[#0F172A] font-bold uppercase tracking-tight">Cities</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b-[4px] border-[#0F172A]">
          <button
            onClick={() => setActiveTab('circles')}
            className={`py-3 px-4 transition-all -mb-[4px] ${
              activeTab === 'circles'
                ? 'text-[#0F172A] bg-[#FFE135] font-black uppercase tracking-tight border-t-[4px] border-x-[4px] border-b-[4px] border-[#0F172A] rounded-t-sm'
                : 'text-[#0F172A] hover:bg-slate-100 font-bold uppercase tracking-tight border-2 border-transparent rounded-t-sm'
            }`}
          >
            <Coffee className="w-4 h-4 inline mr-2" />
            Circles ({circles.length})
          </button>
          <button
            onClick={() => setActiveTab('communities')}
            className={`py-3 px-4 transition-all -mb-[4px] ${
              activeTab === 'communities'
                ? 'text-[#0F172A] bg-[#FFE135] font-black uppercase tracking-tight border-t-[4px] border-x-[4px] border-b-[4px] border-[#0F172A] rounded-t-sm'
                : 'text-[#0F172A] hover:bg-slate-100 font-bold uppercase tracking-tight border-2 border-transparent rounded-t-sm'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Communities ({communities.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E8430A]" />
          </div>
        ) : (
          <>
            {/* Circles Grid */}
            {activeTab === 'circles' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="circles-grid">
                {circles.length === 0 ? (
                  <div className="col-span-full text-center py-16">
                    <Coffee className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-xl text-gray-400 mb-2">No circles found</h3>
                    <p className="text-gray-500">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  circles.map((circle) => (
                    <div
                      key={circle.id}
                      className="group bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-xl shadow-slate-200/50 transition-all"
                      data-testid={`circle-card-${circle.id}`}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 border border-slate-200 bg-[#FFE135] shadow-lg shadow-slate-200/40 rounded-xl flex items-center justify-center">
                            <Coffee className="w-6 h-6 text-[#0F172A]" />
                          </div>
                          <span className="px-3 py-1 bg-white border border-slate-200 text-[#0F172A] font-black uppercase tracking-tight text-xs">
                            {circle.visibility === 'public' ? 'Public' : 'Community'}
                          </span>
                        </div>
                        
                        <h3 className="text-xl font-black text-[#0F172A] mb-2 uppercase tracking-tight group-hover:text-[#E8430A] transition-colors">
                          {circle.name}
                        </h3>
                        
                        <p className="text-sm text-[#0F172A] font-bold mb-4 line-clamp-3 hover:line-clamp-none transition-all cursor-help" title={circle.description}>
                          {circle.description || 'A space for meaningful professional connections.'}
                        </p>
                        
                        <div className="flex items-center gap-4 text-sm text-[#0F172A] font-black uppercase tracking-tight mb-4">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {circle.stats?.total_members || 0}
                          </span>
                          {circle.community?.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {circle.community.city}
                            </span>
                          )}
                        </div>
                        
                        {circle.community?.name && (
                          <div className="flex items-center gap-2 text-xs text-[#0F172A] font-black uppercase tracking-tight mb-4 pb-4 border-b-[4px] border-[#0F172A]">
                            <Building2 className="w-3 h-3" />
                            {circle.community.name}
                          </div>
                        )}
                        
                        <button
                          onClick={() => handleJoinCircle(circle.id, circle.name)}
                          className="w-full py-3 bg-white border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 text-[#0F172A] rounded-xl font-black uppercase tracking-tight transition-all flex items-center justify-center gap-2"
                          data-testid={`join-circle-${circle.id}`}
                        >
                          <UserPlus className="w-4 h-4" />
                          Request to Join
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Communities Grid */}
            {activeTab === 'communities' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="communities-grid">
                {communities.length === 0 ? (
                  <div className="col-span-full text-center py-16">
                    <Building2 className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-xl text-gray-400 mb-2">No communities found</h3>
                    <p className="text-gray-500">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  communities.map((community) => (
                    <div
                      key={community.id}
                      className="group bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-xl shadow-slate-200/50 transition-all"
                      data-testid={`community-card-${community.id}`}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 border border-slate-200 bg-[#C9960A] shadow-lg shadow-slate-200/40 flex items-center justify-center rounded-xl">
                            {community.logo_url ? (
                              <img loading="lazy" src={community.logo_url} alt="" className="w-8 h-8 rounded-xl" />
                            ) : (
                              <Building2 className="w-6 h-6 text-[#0F172A]" />
                            )}
                          </div>
                          {community.type && (
                            <span className="px-3 py-1 bg-white border border-slate-200 text-[#0F172A] font-black uppercase tracking-tight text-xs">
                              {community.type}
                            </span>
                          )}
                        </div>
                        
                        <h3 className="text-xl font-black text-[#0F172A] mb-2 uppercase tracking-tight group-hover:text-[#E8430A] transition-colors">
                          {community.name}
                        </h3>
                        
                        <p className="text-sm text-[#0F172A] font-bold mb-4 line-clamp-3 hover:line-clamp-none transition-all cursor-help" title={community.description}>
                          {community.description || 'A professional community for networking and growth.'}
                        </p>
                        
                        <div className="flex items-center gap-4 text-sm text-[#0F172A] font-black uppercase tracking-tight">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {community.member_count || 0} members
                          </span>
                          <span className="flex items-center gap-1">
                            <Coffee className="w-4 h-4" />
                            {community.circle_count || 0} circles
                          </span>
                          {community.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {community.city}
                            </span>
                          )}
                        </div>
                        
                        <button
                          onClick={() => {
                            setSelectedCommunity(community.id);
                            setActiveTab('circles');
                          }}
                          className="w-full mt-4 py-3 bg-white border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 text-[#0F172A] rounded-xl font-black uppercase tracking-tight transition-all flex items-center justify-center gap-2"
                        >
                          View Circles
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}


          </>
        )}
      </div>

      {/* Start Your Community Section */}
      <div className="border-t-[4px] border-[#0F172A] bg-[#FFFDF7]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-[#0F172A] mb-4 uppercase tracking-tight">
              Bring Your Network to Match It Up
            </h2>
            <p className="text-[#0F172A] font-bold max-w-2xl mx-auto">
              Create a private hub for your alumni network, coworking space, or professional community. 
              Flexible structure - you design it, we power it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Left: Structure Example */}
            <div className="bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-6">
              <h3 className="text-xl font-black text-[#0F172A] mb-4 uppercase tracking-tight flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#E8430A]" />
                How It Works
              </h3>
              <div className="bg-[#FFFDF7] border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-4 font-mono text-sm">
                <div className="text-[#0F172A] font-black uppercase tracking-tight mb-2">Your Community</div>
                <div className="text-[#0F172A] font-bold space-y-1 ml-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[#0F172A]">├──</span>
                    <span>🎓 Batch of 2010</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#0F172A]">├──</span>
                    <span>🎓 Batch of 2015</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#0F172A]">├──</span>
                    <span>🏙️ City: Delhi NCR</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#0F172A]">├──</span>
                    <span>💼 Founders Network</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#0F172A]">└──</span>
                    <span>📢 Opportunities Board</span>
                  </div>
                </div>
              </div>
              <p className="text-[#0F172A] font-bold text-sm mt-4">
                Create unlimited Brew Circles as sub-groups. Admins control access and moderation.
              </p>
            </div>

            {/* Right: Benefits */}
            <div className="space-y-4">
              <h3 className="text-xl font-black text-[#0F172A] mb-4 uppercase tracking-tight flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                What You Get
              </h3>
              <div className="space-y-3">
                {[
                  { icon: '🎯', title: 'Private Brew Circles', desc: 'Create focused sub-groups for batches, cities, or interests' },
                  { icon: '📊', title: 'Admin Dashboard', desc: 'Member analytics, join requests, and moderation tools' },
                  { icon: '🔗', title: 'Branded Invite Codes', desc: 'Custom codes like YOURORG-2024 for easy onboarding' },
                  { icon: '🤝', title: 'AI-Powered Matching', desc: 'Members discover relevant connections automatically' },
                  { icon: '🛡️', title: 'Trust & Verification', desc: 'Profile credibility checks before matching' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-[#FFE135] border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 transition-all rounded-xl p-3">
                    <span className="text-xl">{item.icon}</span>
                    <div>
                      <div className="font-black text-[#0F172A] uppercase tracking-tight text-sm">{item.title}</div>
                      <div className="text-[#0F172A] font-bold text-xs">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Who is this for */}
          <div className="mt-12 text-center">
            <p className="text-[#0F172A] font-black uppercase tracking-tight text-sm mb-4">Perfect for</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['🎓 Alumni Networks', '☕ Cafes & Third Spaces', '🏢 Coworking Spaces', '🎪 Event Organizers', '💼 Professional Associations'].map((item, idx) => (
                <span key={idx} className="px-4 py-2 bg-white border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl font-black uppercase tracking-tight text-sm text-[#0F172A]">
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-[#0F172A] font-black uppercase tracking-tight mb-4">Want to create your community?</p>
            <a
              href="mailto:support@matchitup.in?subject=Community%20Creation%20Request"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFE135] text-[#0F172A] border border-slate-200 shadow-lg shadow-slate-200/50 hover:-translate-y-1 hover:shadow-xl shadow-slate-200/50 rounded-xl font-black uppercase tracking-tight transition-all"
            >
              <Send className="w-5 h-5" />
              Email Us at support@matchitup.in
            </a>
          </div>
        </div>
      </div>

      {/* Partner Programs Section */}
      <div className="border-t-[4px] border-[#0F172A] bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-[#0F172A] mb-4 uppercase tracking-tight">
              Partner Programs
            </h2>
            <p className="text-[#0F172A] font-bold max-w-2xl mx-auto">
              Learn how Match It Up can transform your space, network, or community into a thriving professional hub.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-testid="partner-cards">
            {/* Cafes & Third Spaces Card */}
            <Link
              to="/partners/cafes"
              className="group bg-[#FFFDF7] border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-6 hover:-translate-y-1 hover:shadow-xl shadow-slate-200/50 transition-all duration-300"
              data-testid="partner-card-cafes"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl flex items-center justify-center mb-4">
                <Coffee className="w-7 h-7 text-[#0F172A]" />
              </div>
              <h3 className="text-2xl font-black text-[#0F172A] mb-2 uppercase tracking-tight group-hover:text-[#E8430A] transition-colors">
                Cafes & Third Spaces
              </h3>
              <p className="text-[#0F172A] font-bold text-sm mb-4">
                Transform your cafe into a community hub. One QR code, viral growth, and loyal customers who keep coming back.
              </p>
              <div className="flex items-center gap-2 text-[#0F172A] font-black uppercase tracking-tight text-sm">
                Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Alumni Networks Card */}
            <Link
              to="/partners/alumni"
              className="group bg-[#FFFDF7] border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-6 hover:-translate-y-1 hover:shadow-xl shadow-slate-200/50 transition-all duration-300"
              data-testid="partner-card-alumni"
            >
              <div className="w-14 h-14 bg-white border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-[#0F172A]" />
              </div>
              <h3 className="text-2xl font-black text-[#0F172A] mb-2 uppercase tracking-tight group-hover:text-[#E8430A] transition-colors">
                Alumni Networks
              </h3>
              <p className="text-[#0F172A] font-bold text-sm mb-4">
                Your alumni network deserves more than a database. Turn passive records into active opportunity discovery.
              </p>
              <div className="flex items-center gap-2 text-[#0F172A] font-black uppercase tracking-tight text-sm">
                Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Coworking Spaces Card */}
            <Link
              to="/partners/coworking"
              className="group bg-[#FFFDF7] border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-6 hover:-translate-y-1 hover:shadow-xl shadow-slate-200/50 transition-all duration-300"
              data-testid="partner-card-coworking"
            >
              <div className="w-14 h-14 bg-[#E8430A] border border-slate-200 shadow-lg shadow-slate-200/40 rounded-xl flex items-center justify-center mb-4">
                <Building2 className="w-7 h-7 text-[#0F172A]" />
              </div>
              <h3 className="text-2xl font-black text-[#0F172A] mb-2 uppercase tracking-tight group-hover:text-[#E8430A] transition-colors">
                Coworking Spaces
              </h3>
              <p className="text-[#0F172A] font-bold text-sm mb-4">
                Intent over chance. Network over desk. Your space transforms into a live professional network.
              </p>
              <div className="flex items-center gap-2 text-[#0F172A] font-black uppercase tracking-tight text-sm">
                Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Join Request Modal */}
      {showJoinModal && joinCircle && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowJoinModal(false)}
          data-testid="join-request-modal"
        >
          <div 
            className="bg-white border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl w-full max-w-lg p-6 font-mono"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl font-black text-[#0F172A] flex items-center gap-2 uppercase tracking-tight">
                  <UserPlus className="w-5 h-5 text-[#E8430A]" />
                  Request to Join
                </h3>
                <p className="text-[#0F172A] font-bold mt-1">{joinCircle.name}</p>
              </div>
              <button 
                onClick={() => setShowJoinModal(false)}
                className="text-[#0F172A] hover:bg-[#E8430A] hover:text-white border border-slate-200 transition-all rounded-xl p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-[#FFE135] border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-4 text-sm text-[#0F172A] font-bold mb-4">
                <p>This is a private Brew Circle. Share why you'd like to join and how you can contribute to the community.</p>
              </div>

              <div>
                <label className="block text-sm font-black text-[#0F172A] uppercase tracking-tight mb-2">
                  Why do you want to join? *
                </label>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="I am a founder building in AI and would love to connect with other builders..."
                  className="w-full bg-[#FFFDF7] border border-slate-200 shadow-xl shadow-slate-200/50 rounded-xl p-3 text-[#0F172A] font-bold placeholder-slate-400 focus:outline-none focus:translate-y-[2px] focus:shadow-lg shadow-slate-200/40 h-32 resize-none transition-all"
                  maxLength={500}
                  required
                />
                <div className="text-right text-xs font-bold text-[#0F172A] mt-1">
                  {justification.length}/500
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowJoinModal(false)}
                className="flex-1 py-3 bg-white border border-slate-200 shadow-xl shadow-slate-200/50 text-[#0F172A] rounded-xl font-black uppercase tracking-tight hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitJoinRequest}
                disabled={submittingRequest || !justification.trim()}
                className="flex-1 py-3 bg-[#FFE135] text-[#0F172A] border border-slate-200 shadow-xl shadow-slate-200/50 hover:-translate-y-1 hover:shadow-lg shadow-slate-200/40 rounded-xl font-black uppercase tracking-tight transition-all disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                data-testid="submit-join-request"
              >
                {submittingRequest ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscoverPage;
